import React, { useState } from "react";
import { useCampaignBudgetCalculations } from "@/hooks/useCampaignBudgetCalculations";
import { useAuth } from "@/contexts/AuthContext";
import { formatCurrency } from "@/utils/currency";
import {
  type CampaignStatsCardsProps,
  type TransformedBrand,
  type TransformedCampaign,
  type TransformedInfluencer,
  getProgressDetails,
  calculateCommittedFees,
  ProgressPanel,
  BudgetPanel,
  InfluencersPanel,
  ObjectiveBar,
  StatsFooter,
} from "./stats";

export type {
  TransformedBrand,
  TransformedCampaign,
  TransformedInfluencer,
  CampaignStatsCardsProps,
};

type Props = CampaignStatsCardsProps;

export const CampaignStatsCards: React.FC<Props> = ({
  campaign,
  rawCampaign,
  influencers = [],
  onBulkEmail,
}) => {
  const { userCountry } = useAuth();
  const [showInfluencersTable, setShowInfluencersTable] = useState(false);
  const [viewBriefOpen, setViewBriefOpen] = useState(false);

  const { data: budgetData, isLoading: loadingBudget } =
    useCampaignBudgetCalculations(campaign.id);

  // Fallbacks if RPC fails
  const displayBudget = parseFloat(
    campaign.totalBudget?.replace(/[₹$,]/g, "") || "0"
  );
  const totalBudget = budgetData?.totalBudget ?? displayBudget;
  const amountSpent = budgetData?.amountSpent ?? 0;
  const remainingBudget =
    budgetData?.remainingBudget ?? (totalBudget - amountSpent);

  // Progress Calculations (Completed Days)
  const startStr =
    campaign.startDate || (rawCampaign as { start_date?: string } | null | undefined)?.start_date;
  const endStr =
    campaign.endDate || (rawCampaign as { end_date?: string } | null | undefined)?.end_date;
  const progress = getProgressDetails(startStr, endStr, campaign.status);

  // Allocation & Ratios
  const totalCommitted = calculateCommittedFees(influencers);
  const allocationPercent =
    totalBudget > 0 ? Math.round((totalCommitted / totalBudget) * 100) : 0;
  const spentPercent =
    totalBudget > 0 ? ((amountSpent / totalBudget) * 100).toFixed(1) : "0";
  const remainingPercent =
    totalBudget > 0 ? ((remainingBudget / totalBudget) * 100).toFixed(1) : "0";

  const handleFormatCurrency = (value: number) => {
    return formatCurrency(value, userCountry);
  };

  return (
    <div className="w-full bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
      {/* Top row: 5 panels */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6">
        <ProgressPanel progress={progress} />

        <BudgetPanel
          loadingBudget={loadingBudget}
          totalBudget={totalBudget}
          amountSpent={amountSpent}
          remainingBudget={remainingBudget}
          allocationPercent={allocationPercent}
          spentPercent={spentPercent}
          remainingPercent={remainingPercent}
          userCountry={userCountry}
          formatCurrency={handleFormatCurrency}
        />

        <InfluencersPanel
          influencers={influencers}
          showInfluencersTable={showInfluencersTable}
          onToggleTable={() => setShowInfluencersTable(!showInfluencersTable)}
        />
      </div>

      {/* Bottom Section */}
      <div className="border-t border-slate-200 p-6 space-y-6">
        <ObjectiveBar
          description={campaign.description}
          viewBriefOpen={viewBriefOpen}
          onViewBriefOpenChange={setViewBriefOpen}
        />

        <StatsFooter
          createdAt={rawCampaign?.created_at}
          updatedAt={rawCampaign?.updated_at}
          onBulkEmail={onBulkEmail}
        />
      </div>
    </div>
  );
};

export default CampaignStatsCards;
